import React, { useState, useRef, useEffect } from 'react'
import { Stage, Layer, Line, Rect, Ellipse, Shape } from 'react-konva';
import { Box, styled, useTheme, IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close';
import ColorPicker from './ColorPicker';
import PenIcon from '@mui/icons-material/Create';
import RectangleIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import TriangleIcon from '@mui/icons-material/ChangeHistory';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CircleIcon from '@mui/icons-material/PanoramaFishEye';
import { useTranslation } from 'react-i18next';

const TOOLBAR_WIDTH = 52;
const TAB_BAR_HEIGHT = 42;

const DrawingBoard = () => {
    const theme = useTheme();
    const [selectedColor, setSelectedColor] = useState(() => theme.palette.mode === 'dark' ? '#ffffff' : '#000000')
    const [selectedTool, setSelectedTool] = useState(false)
    const [selectedStrock, setSelectedStrock] = useState(false)
    const [lines, setLines] = useState([]);
    const [rectangles, setRectangles] = useState([]);
    const [triangles, setTriangles] = useState([]);
    const [ellipses, setEllipses] = useState([]);
    const [strightLines, setStrightLines] = useState([]);
    const [markerLines, setMarkerLines] = useState([]);
    const [showCanvas, setShowCanvas] = useState(true);
    const [stageWidth, setStageWidth] = useState(200);
    const [stageHeight, setStageHeight] = useState(200);
    const [strokeWidth, setStrokeWidth] = useState(2)
    const isDrawing = useRef(false);
    const containerRef = useRef();
    const menuBoxRef = useRef()
    const strockMenuBoxRef = useRef()
    const menuAnchorRef = useRef()
    const storckMenuAnchorRef = useRef()
    const handleOpenMenu = () => {
        menuBoxRef.current.style.visibility = 'visible';
        menuBoxRef.current.style.top = menuAnchorRef.current.offsetTop + 25 + 'px';
        menuBoxRef.current.style.left = theme.direction === 'rtl' ? menuAnchorRef.current.offsetLeft - 25 + 'px' : menuAnchorRef.current.offsetLeft + 25 + 'px';
    };
    const handleOpenStrockMenu = () => {
        strockMenuBoxRef.current.style.visibility = 'visible';
        strockMenuBoxRef.current.style.top = strockMenuBoxRef.current.offsetTop + 25 + 'px';
        strockMenuBoxRef.current.style.left = theme.direction === 'rtl' ? strockMenuBoxRef.current.offsetLeft - 25 + 'px' : strockMenuBoxRef.current.offsetLeft + 25 + 'px';
    };
    const handleMenuClose = () => {
        menuBoxRef.current.style.visibility = 'hidden';
    };
    const handleStrockMenuClose = () => {
        strockMenuBoxRef.current.style.visibility = 'hidden';
    };
    const handleOptionClick = (option) => {
        setSelectedTool(option);
        handleMenuClose();
    };
    const handleStrockOptionClick = (option) => {
        setSelectedStrock(option);
        handleStrockMenuClose();
    };
    const handleMouseDown = (e) => {
        isDrawing.current = true;
        const pos = e.target.getStage().getPointerPosition();
        if (selectedTool) {
            switch (selectedTool.label) {
                case 'Pen':
                    setLines([...lines, { tool: selectedTool.label, points: [pos.x, pos.y], color: selectedColor, strokeWidth }]);
                    break;
                case 'Rectangle':
                    setRectangles([...rectangles, { tool: selectedTool.label, type: 'rectangle', x: pos.x, y: pos.y, width: 0, height: 0, color: selectedColor, strokeWidth }])
                    break;
                case 'Triangle':
                    setTriangles([...triangles, { tool: selectedTool.label, type: 'triangle', firstPoint: [pos.x, pos.y], secondPoint: [pos.x, pos.y], thirdPoint: [pos.x, pos.y], color: selectedColor, strokeWidth }])
                    break;
                case 'Ellipse':
                    setEllipses([...ellipses, { tool: selectedTool.label, type: 'ellipse', x: pos.x, y: pos.y, radiusX: 0, radiusY: 0, color: selectedColor, strokeWidth }])
                    break;
                case 'Line':
                    setStrightLines([...strightLines, { tool: selectedTool.label, type: 'line', points: [pos.x, pos.y], color: selectedColor, strokeWidth }])
                    break;
                case 'Marker':
                    setMarkerLines([...markerLines, { tool: selectedTool.label, points: [pos.x, pos.y], color: selectedColor, strokeWidth: 15 }]);
                    break;
            }
        }

    };

    const handleMouseMove = (e) => {
        if (!isDrawing.current) {
            return;
        }
        const stage = e.target.getStage();

        const point = stage.getPointerPosition();
        switch (selectedTool.label) {
            case 'Pen':
                setLines(previous => {
                    const lastLine = previous[previous.length - 1];
                    if (!lastLine) return previous;
                    return [
                        ...previous.slice(0, -1),
                        { ...lastLine, points: [...lastLine.points, point.x, point.y] },
                    ];
                });
                break;
            case 'Rectangle':
                setRectangles(previous => {
                    const lastRectangle = previous[previous.length - 1];
                    if (!lastRectangle) return previous;
                    return [
                        ...previous.slice(0, -1),
                        {
                            ...lastRectangle,
                            width: Math.max(0, point.x - lastRectangle.x),
                            height: Math.max(0, point.y - lastRectangle.y),
                        },
                    ];
                });
                break;
            case 'Triangle':
                setTriangles(previous => {
                    const lastTriangle = previous[previous.length - 1];
                    if (!lastTriangle) return previous;
                    return [
                        ...previous.slice(0, -1),
                        {
                            ...lastTriangle,
                            secondPoint: [point.x, point.y],
                            thirdPoint: [point.x - (point.x - lastTriangle.firstPoint[0]) * 2, point.y],
                        },
                    ];
                });
                break;
            case 'Ellipse':
                setEllipses(previous => {
                    const lastEllipse = previous[previous.length - 1];
                    if (!lastEllipse) return previous;
                    return [
                        ...previous.slice(0, -1),
                        {
                            ...lastEllipse,
                            radiusX: Math.max(0, point.x - lastEllipse.x),
                            radiusY: Math.max(0, point.y - lastEllipse.y),
                        },
                    ];
                });
                break;
            case 'Line':
                setStrightLines(previous => {
                    const lastStraightLine = previous[previous.length - 1];
                    if (!lastStraightLine) return previous;
                    return [
                        ...previous.slice(0, -1),
                        {
                            ...lastStraightLine,
                            points: [lastStraightLine.points[0], lastStraightLine.points[1], point.x, point.y],
                        },
                    ];
                });
                break;
            case 'Marker':
                setMarkerLines(previous => {
                    const lastMarker = previous[previous.length - 1];
                    if (!lastMarker) return previous;
                    return [
                        ...previous.slice(0, -1),
                        { ...lastMarker, points: [...lastMarker.points, point.x, point.y] },
                    ];
                });
                break;
        }
    };
    useEffect(() => {
        if (selectedTool) { setShowCanvas(true) } else { setShowCanvas(false) }
    }, [selectedTool])
    useEffect(() => {
        const stageHost = containerRef.current?.parentElement;
        if (!stageHost) return undefined;

        const updateStageSize = () => {
            const bounds = stageHost.getBoundingClientRect();
            setStageWidth(Math.max(1, Math.floor(bounds.width - TOOLBAR_WIDTH)));
            setStageHeight(Math.max(1, Math.floor(bounds.height - TAB_BAR_HEIGHT)));
        };
        updateStageSize();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateStageSize);
        observer?.observe(stageHost);
        window.addEventListener('resize', updateStageSize);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', updateStageSize);
        };
    }, []);
    const handleMouseUp = () => {
        isDrawing.current = false;
    };
    const ClassRoomToolBox = styled(Box)({
        display: 'flex',
        width: '100%',
        height: '100%',
        paddingTop: '10px',
        boxSizing: 'border-box',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '12px',
        position: 'relative',
        overflowY: 'auto',

    })
    const handleDeleteShapes = () => {
        setShowCanvas(false);
        setSelectedTool(false);
        setLines([]);
        setRectangles([])
        setTriangles([])
        setEllipses([])
        setStrightLines([])
        setMarkerLines([])
    }
    const DrawingBoxContainer = styled(Box)({
        flex: `0 0 ${TOOLBAR_WIDTH}px`,
        width: `${TOOLBAR_WIDTH}px`,
        height: '100%',
        minHeight: 0,
        zIndex: 3,
        borderLeft: theme.direction === 'rtl' ? `1px solid ${theme.palette.text.secondary}` : 'none',
        borderRight: theme.direction === 'ltr' ? `1px solid ${theme.palette.text.secondary}` : 'none',

    })
    const SmallStrockBox = styled(Box)({
        width: '20px',
        height: '2px',
        backgroundColor: theme.palette.text.secondary,
        cursor: 'pointer',
        margin: '8px'

    })
    const MediumStrockBox = styled(Box)({
        width: '20px',
        height: '4px',
        backgroundColor: theme.palette.text.secondary,
        cursor: 'pointer',
        margin: '8px'

    })
    const LargStrockBox = styled(Box)({
        width: '20px',
        height: '6px',
        backgroundColor: theme.palette.text.secondary,
        cursor: 'pointer',
        margin: '8px'

    })
    const CustomMenuBox = styled(Box)({
        position: 'absolute',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        visibility: 'hidden',
        background: theme.palette.background.default,
        border: `1px solid ${theme.palette.text.secondary}`,
        borderRadius: '5px'
    })
    return (
        <DrawingBoxContainer ref={containerRef} data-testid="drawing-toolbar">
            <ClassRoomToolBox>

                <IconButton ref={menuAnchorRef} onClick={handleOpenMenu}>
                    {selectedTool ? selectedTool.icon : <PenIcon />}
                </IconButton>

                <CustomMenuBox ref={menuBoxRef}>

                    <IconButton onClick={() => handleOptionClick({ label: 'Pen', icon: <PenIcon /> })}>
                        <PenIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOptionClick({ label: 'Marker', icon: <BorderColorIcon /> })}>
                        <BorderColorIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOptionClick({ label: 'Line', icon: <HorizontalRuleIcon /> })}>
                        <HorizontalRuleIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOptionClick({ label: 'Rectangle', icon: <RectangleIcon /> })}>
                        <RectangleIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOptionClick({ label: 'Ellipse', icon: <CircleIcon /> })}>
                        <CircleIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOptionClick({ label: 'Triangle', icon: <TriangleIcon /> })}>
                        <TriangleIcon />
                    </IconButton>
                </CustomMenuBox>

                <IconButton ref={storckMenuAnchorRef} onClick={handleOpenStrockMenu}>
                    {selectedStrock ? selectedStrock.icon : <SmallStrockBox />}
                </IconButton>

                <CustomMenuBox ref={strockMenuBoxRef}>

                    <SmallStrockBox onClick={() => handleStrockOptionClick({ label: 'Small', icon: <SmallStrockBox />, strockWidth: 2 })}>
                    </SmallStrockBox>
                    <MediumStrockBox onClick={() => handleStrockOptionClick({ label: 'Medium', icon: <MediumStrockBox />, strockWidth: 4 })}>
                    </MediumStrockBox>
                    <LargStrockBox onClick={() => handleStrockOptionClick({ label: 'Large', icon: <LargStrockBox />, strockWidth: 6 })}>
                    </LargStrockBox>

                </CustomMenuBox>

                <ColorPicker onSelectColor={(color) => setSelectedColor(color)} colorSelected={selectedColor} />

                <IconButton onClick={() => handleDeleteShapes()}>
                    <CloseIcon />
                </IconButton>
            </ClassRoomToolBox>

            {showCanvas && stageHeight && stageWidth && (
                <Box
                    data-testid="drawing-layer"
                    sx={{
                        position: 'absolute',
                        insetBlockStart: `${TAB_BAR_HEIGHT}px`,
                        insetBlockEnd: 0,
                        insetInlineStart: `${TOOLBAR_WIDTH}px`,
                        insetInlineEnd: 0,
                        overflow: 'hidden',
                        zIndex: 2,
                    }}
                >
                    <Stage width={stageWidth} height={stageHeight} style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'block',
                    }}
                        onPointerDown={handleMouseDown}
                        onPointerMove={handleMouseMove}
                        onPointerUp={handleMouseUp}
                    >
                        <Layer>
                            {lines.map((line, i) => (
                                <Line
                                    key={i}
                                    points={line.points}
                                    stroke={line.color}
                                    strokeWidth={selectedStrock?.strockWidth ?? 2}
                                    tension={0.1}
                                    lineCap="round"
                                    lineJoin="round"
                                    globalCompositeOperation={
                                        line.tool === 'eraser' ? 'destination-out' : 'source-over'
                                    }
                                />
                            ))}
                            {strightLines.map((line, i) => (
                                <Line
                                    key={i}
                                    points={line.points}
                                    stroke={line.color}
                                    strokeWidth={selectedStrock?.strockWidth ?? 2}
                                    tension={0.1}
                                    lineCap="round"
                                    lineJoin="round"
                                    globalCompositeOperation={
                                        line.tool === 'eraser' ? 'destination-out' : 'source-over'
                                    }
                                />
                            ))}
                            {markerLines.map((line, i) => (
                                <Line
                                    key={i}
                                    points={line.points}
                                    stroke={line.color}
                                    strokeWidth={15}
                                    tension={0.1}
                                    opacity={0.3}
                                    lineCap='square'
                                    lineJoin="round"
                                    globalCompositeOperation={
                                        line.tool === 'eraser' ? 'destination-out' : 'source-over'
                                    }
                                />
                            ))}
                            {rectangles.map((rect, i) => (

                                <Rect key={i} x={rect.x} y={rect.y} width={rect.width} height={rect.height} stroke={rect.color} strokeWidth={selectedStrock?.strockWidth ?? 2} />

                            ))}
                            {triangles.map((triangle, i) => (
                                <Shape
                                    sceneFunc={(context, shape) => {
                                        context.beginPath();
                                        context.moveTo(triangle.firstPoint[0], triangle.firstPoint[1]);
                                        context.lineTo(triangle.secondPoint[0], triangle.secondPoint[1]);
                                        context.lineTo(triangle.thirdPoint[0], triangle.thirdPoint[1]);
                                        context.closePath();
                                        context.fillStrokeShape(shape);
                                    }}
                                    stroke={triangle.color}
                                    strokeWidth={selectedStrock?.strockWidth ?? 2}
                                />
                            ))}
                            {ellipses.map((ellipse, i) => (
                                <Ellipse
                                    key={i}
                                    x={ellipse.x}
                                    y={ellipse.y}
                                    radiusX={ellipse.radiusX}
                                    radiusY={ellipse.radiusY}
                                    stroke={ellipse.color}
                                    strokeWidth={selectedStrock?.strockWidth ?? 2}
                                />
                            ))}
                        </Layer>
                    </Stage>
                </Box>
            )}
        </DrawingBoxContainer>

    )
}

export default DrawingBoard
